import React from "react";
import { Search } from "lucide-react";

/**
 * 앱 셸 사이드바(CRM · IT-CONSULTING · 설정) 공용 모양 — Vault UI.
 *  - 섹션 제목: 12px/600 ink-muted
 *  - 메뉴 항목: 32px(접힘 36px) · 모서리 6px · 13px · 글자 ink-data · 아이콘 ink-muted
 *    활성 = primary-subtle 면 + primary-strong 글자 600 + 아이콘 primary
 *  - 메뉴 검색: 오른쪽에 단축키 칩(⌘K / Ctrl K)
 * hover 면은 인라인으로 못 거니 항목에 SIDE_ITEM_HOVER_CLASS 를 붙인다(활성일 때는 빼야 활성 면이 남는다).
 */

export const sideSectionLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0,
  color: "var(--ink-muted)",
};

export const SIDE_ITEM_HOVER_CLASS = "hover:bg-surface-hover hover:text-ink";

/** 메뉴 항목 인라인 스타일. hover 는 SIDE_ITEM_HOVER_CLASS. */
export function sideItemStyle(isActive: boolean, collapsed: boolean, indent = false): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: collapsed ? 0 : 9,
    height: collapsed ? 36 : 32,
    padding: collapsed ? "0" : "0 10px",
    paddingLeft: !collapsed && indent ? 28 : collapsed ? 0 : 10,
    justifyContent: collapsed ? "center" : "flex-start",
    margin: collapsed ? "2px 8px" : "1px 8px",
    borderRadius: 6,
    textDecoration: "none",
    cursor: "pointer",
    background: isActive ? "var(--primary-subtle)" : undefined,
    color: isActive ? "var(--primary-strong)" : "var(--ink-data)",
    fontWeight: isActive ? 600 : 400,
    fontSize: 13,
    transition: "background 0.12s, color 0.12s",
    position: "relative",
    // 접힘(아이콘만)에서는 움직이는 막대가 없어 왼쪽 안쪽 선으로 활성을 표시한다.
    boxShadow: isActive && collapsed ? "inset 3px 0 0 var(--primary)" : "none",
  };
}

/** 메뉴 아이콘 색 — 활성 primary, 기본 ink-muted. */
export function sideItemIconColor(isActive: boolean): string {
  return isActive ? "var(--primary)" : "var(--ink-muted)";
}

// 메뉴 검색 단축키 표시 — Mac 은 ⌘K, 그 외 Ctrl K. (키 판정은 metaKey || ctrlKey 둘 다 받는다)
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || "");
export const MENU_SEARCH_SHORTCUT_LABEL = IS_MAC ? "⌘K" : "Ctrl K";

/** Cmd/Ctrl+K 인지 — Shift·Alt 조합이나 다른 키는 건드리지 않는다(다른 입력칸 타이핑 보존). */
export function isMenuSearchShortcut(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === "k" || e.key === "K");
}

/** 사이드바 메뉴 검색 칸 — 오른쪽에 단축키 칩. 값이 있으면 칩을 숨긴다. Esc 로 지운다. */
export function MenuSearchInput({
  inputRef,
  value,
  onChange,
  placeholder,
}: {
  inputRef: React.Ref<HTMLInputElement>;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div style={{ position: "relative" }}>
      <Search size={14} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "var(--ink-disabled)", pointerEvents: "none" }} />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && value) {
            e.stopPropagation();
            onChange("");
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        aria-keyshortcuts="Meta+K Control+K"
        className="box-border h-8 w-full rounded-control border border-solid border-line bg-white pl-[30px] pr-[52px] font-[inherit] text-[13px] text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--primary-subtle)]"
      />
      {!value ? (
        <kbd
          aria-hidden
          className="pointer-events-none absolute right-[7px] top-1/2 box-border h-[18px] -translate-y-1/2 rounded-badge border border-solid border-hairline-strong bg-surface-soft px-[5px] font-[inherit] text-[12px] leading-4 text-ink-muted"
        >
          {MENU_SEARCH_SHORTCUT_LABEL}
        </kbd>
      ) : null}
    </div>
  );
}
